import { ref, inject } from "vue"
import { handleError } from "@shared/helpers/utils"
import { successMessage } from "@shared/helpers/message"
import type { Help, HelpListItem, HelpMenuItem, HelpView } from "../types"

export const useHelp = () => {
  const axios: any = inject("$baseHttpClient")
  const isLoading = ref(false)

  const getAll = async (): Promise<HelpListItem[]> => {
    isLoading.value = true
    try {
      const { data } = await axios.get("/Help")
      return data ?? []
    } catch (err) {
      handleError(err)
      return []
    } finally {
      isLoading.value = false
    }
  }

  const getByUniqueId = async (uniqueId: string): Promise<Help | null> => {
    isLoading.value = true
    try {
      const { data } = await axios.get(`/Help/${uniqueId}`)
      return data ?? null
    } catch (err) {
      handleError(err)
      return null
    } finally {
      isLoading.value = false
    }
  }

  const create = async (dto: Help): Promise<Help | null> => {
    isLoading.value = true
    try {
      const payload = Object.fromEntries(Object.entries(dto).filter(([k]) => k !== "uniqueId"))
      const { data } = await axios.post("/Help", payload)
      if (!data?.success) {
        handleError(data)
        return null
      }
      successMessage("admin.help.messages.created")
      return data.result ?? null
    } catch (err) {
      handleError(err)
      return null
    } finally {
      isLoading.value = false
    }
  }

  const update = async (dto: Help): Promise<Help | null> => {
    isLoading.value = true
    try {
      const { data } = await axios.put("/Help", dto)
      if (!data?.success) {
        handleError(data)
        return null
      }
      successMessage("admin.help.messages.updated")
      return data.result ?? null
    } catch (err) {
      handleError(err)
      return null
    } finally {
      isLoading.value = false
    }
  }

  const remove = async (uniqueId: string): Promise<boolean> => {
    isLoading.value = true
    try {
      const { data } = await axios.delete(`/Help/${uniqueId}`)
      if (!data?.success) {
        handleError(data)
        return false
      }
      successMessage("admin.help.messages.deleted")
      return true
    } catch (err) {
      handleError(err)
      return false
    } finally {
      isLoading.value = false
    }
  }

  const updateDisplayOrder = async (items: { id: number; displayOrder: number }[]): Promise<boolean> => {
    isLoading.value = true
    try {
      const { data } = await axios.put("/Help/DisplayOrder", { items })
      if (data?.success) {
        successMessage("admin.help.messages.updated")
        return true
      }
      handleError(data)
      return false
    } catch (err) {
      handleError(err)
      return false
    } finally {
      isLoading.value = false
    }
  }

  const getMenu = async (): Promise<HelpMenuItem[]> => {
    try {
      const { data } = await axios.get("/Help/menu")
      return data ?? []
    } catch (err) {
      handleError(err)
      return []
    }
  }

  const getView = async (uniqueId: string): Promise<HelpView | null> => {
    isLoading.value = true
    try {
      const { data } = await axios.get(`/Help/view/${uniqueId}`)
      return data ?? null
    } catch (err) {
      handleError(err)
      return null
    } finally {
      isLoading.value = false
    }
  }

  const trackView = async (uniqueId: string): Promise<void> => {
    try {
      await axios.post(`/Help/view/${uniqueId}/track`)
    } catch {
      // a tracking failure must never break the page
    }
  }

  const submitFeedback = async (uniqueId: string, isHelpful: boolean | null): Promise<boolean> => {
    try {
      await axios.post(`/Help/view/${uniqueId}/feedback`, null, {
        params: isHelpful !== null ? { isHelpful } : {},
      })
      return true
    } catch (err) {
      handleError(err)
      return false
    }
  }

  return {
    isLoading,
    getAll,
    getByUniqueId,
    create,
    update,
    remove,
    updateDisplayOrder,
    getMenu,
    getView,
    trackView,
    submitFeedback,
  }
}
